import React, { useState, useEffect, useRef } from 'react';

/**
 * VideoFacade - Performance-optimized video component
 * 
 * Shows a high-quality WebP thumbnail first, then loads the actual video:
 * - On user interaction (hover/click) OR
 * - After 3 seconds of page load
 * 
 * This reduces initial page load time and bandwidth usage.
 */
const VideoFacade = ({
  videoSrc,
  thumbnailSrc,
  alt = 'Video background',
  className = '',
  videoClassName = 'w-full h-full object-cover opacity-30',
  thumbnailClassName = 'w-full h-full object-cover',
  videoStyle = { filter: 'grayscale(100%) contrast(1.2)' },
  videoType = 'video/webm',
  autoPlay = true,
  loop = true,
  muted = true,
  playsInline = true,
  onVideoLoaded,
  ...props
}) => {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const timeoutRef = useRef(null);

  // Auto-load after 3 seconds
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (!hasInteracted) {
        setShouldLoadVideo(true);
      }
    }, 3000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [hasInteracted]);

  // Handle user interaction (hover or click)
  const handleInteraction = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
      setShouldLoadVideo(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  // Handle video loaded event
  const handleVideoLoaded = () => {
    setVideoReady(true);
    if (onVideoLoaded) {
      onVideoLoaded();
    }
  };

  // Preload video when shouldLoadVideo becomes true
  useEffect(() => {
    if (shouldLoadVideo && videoRef.current) {
      // Force video to load
      videoRef.current.load();
    }
  }, [shouldLoadVideo]);

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      onMouseEnter={handleInteraction}
      onClick={handleInteraction}
      {...props}
    >
      {/* Thumbnail - shown initially and during video load */}
      {(!shouldLoadVideo || !videoReady) && thumbnailSrc && (
        <img
          src={thumbnailSrc}
          alt={alt}
          className={thumbnailClassName}
          loading="eager"
          decoding="async"
        />
      )}

      {/* Video - loaded on demand */}
      {shouldLoadVideo && (
        <video
          ref={videoRef}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          className={videoClassName}
          style={{
            ...videoStyle,
            opacity: videoReady ? (videoStyle.opacity !== undefined ? videoStyle.opacity : 0.3) : 0,
            transition: 'opacity 0.5s ease-in-out',
          }}
          onLoadedData={handleVideoLoaded}
          onCanPlay={handleVideoLoaded}
          preload="metadata"
        >
          <source src={videoSrc} type={videoType} />
          {/* Fallback message for browsers that don't support video */}
          Your browser does not support the video tag.
        </video>
      )}

      {/* Loading indicator (optional, can be removed if not needed) */}
      {shouldLoadVideo && !videoReady && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

export default VideoFacade;
